// Copied from d2d-XDR (siem-frontend) via d2d-UEM, with d2d-UEM's two changes:
// toast() goes through ds-dlp's ToastProvider, the app's one toast stack,
// rather than drawing a second one; and the prompt's submit is the ink or
// danger Button, not a button painted in the variant colour.
//
// Global in-app dialog system: toasts, confirmation dialogs, and form/prompt
// dialogs — themed with the existing --d2d design tokens. Exposes an imperative
// async API (useDialogs) so callers replace the blocking browser dialogs with a
// one-line await and keep their control flow unchanged:
//   window.alert(msg)            -> toast(msg)
//   if (window.confirm(msg))     -> if (await confirm({ message: msg }))
//   const x = window.prompt(q,d) -> const x = (await prompt({ fields:[...] }))?.value

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Modal, Textarea } from "@/components/ds";
import { Button, Input, Select } from "@/components/ds";
import { useToast } from "@/components/ds-dlp";

type ToastVariant = "success" | "error" | "info" | "warning";
type DialogVariant = "danger" | "warning" | "info" | "success";

type ConfirmOptions = {
  title?: string;
  message: string;
  variant?: DialogVariant;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Typed confirmation for actions that are hard to undo (fleet-wide switches,
   *  irreversible deletes): the confirm button stays disabled until the user
   *  types this exact text. */
  confirmText?: string;
};

export type PromptField = {
  name: string;
  label?: string;
  type?: "text" | "textarea" | "select" | "number" | "password";
  required?: boolean;
  placeholder?: string;
  defaultValue?: string;
  options?: { value: string; label: string }[];
  help?: string;
};

type PromptOptions = {
  title?: string;
  description?: string;
  fields: PromptField[];
  submitLabel?: string;
  cancelLabel?: string;
  variant?: DialogVariant;
};

type DialogApi = {
  toast: (message: string, opts?: { variant?: ToastVariant }) => void;
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  prompt: (opts: PromptOptions) => Promise<Record<string, string> | null>;
};

const DialogContext = createContext<DialogApi | null>(null);

export function useDialogs(): DialogApi {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error("useDialogs must be used within <DialogProvider>");
  return ctx;
}

export function DialogProvider({ children }: { children: React.ReactNode }) {
  const { toast: pushToast } = useToast();
  const [confirmState, setConfirmState] = useState<{ opts: ConfirmOptions; resolve: (v: boolean) => void } | null>(null);
  const [promptState, setPromptState] = useState<{ opts: PromptOptions; resolve: (v: Record<string, string> | null) => void } | null>(null);

  const toast = useCallback(
    (message: string, opts?: { variant?: ToastVariant }) => {
      const v = opts?.variant ?? "info";
      pushToast({ description: message, variant: v === "error" ? "danger" : v });
    },
    [pushToast]
  );

  const confirm = useCallback((opts: ConfirmOptions) =>
    new Promise<boolean>(resolve => setConfirmState({ opts, resolve })), []);

  const prompt = useCallback((opts: PromptOptions) =>
    new Promise<Record<string, string> | null>(resolve => setPromptState({ opts, resolve })), []);

  return (
    <DialogContext.Provider value={{ toast, confirm, prompt }}>
      {children}
      {confirmState && (
        <ConfirmDialog
          {...confirmState.opts}
          onResult={ok => { confirmState.resolve(ok); setConfirmState(null); }}
        />
      )}
      {promptState && (
        <PromptDialog
          {...promptState.opts}
          onResult={vals => { promptState.resolve(vals); setPromptState(null); }}
        />
      )}
    </DialogContext.Provider>
  );
}

// ── Confirm dialog ────────────────────────────────────────────────────────────
function ConfirmDialog({ title = "Confirm action", message, variant = "danger", confirmLabel = "Confirm", cancelLabel = "Cancel", confirmText, onResult }:
  ConfirmOptions & { onResult: (ok: boolean) => void }) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [typed, setTyped] = useState("");
  const matches = !confirmText || typed.trim() === confirmText;
  useEffect(() => {
    // With a typed confirmation, start in the field rather than on the button.
    (confirmText ? inputRef.current : buttonRef.current)?.focus();
  }, [confirmText]);
  return (
    <Modal
      open
      onClose={() => onResult(false)}
      title={title}
      size="sm"
      footer={
        <div className="flex justify-end gap-2 w-full">
          <Button variant="secondary" size="sm" onClick={() => onResult(false)}>
            {cancelLabel}
          </Button>
          <Button
            ref={buttonRef}
            onClick={() => onResult(true)}
            size="sm"
            disabled={!matches}
            variant={variant === "danger" ? "danger" : "primary"}
          >
            {confirmLabel}
          </Button>
        </div>
      }
    >
      {confirmText ? (
        <form
          className="space-y-4"
          onSubmit={e => { e.preventDefault(); if (matches) onResult(true); }}
        >
          <p>{message}</p>
          <Input
            ref={inputRef}
            label={`Type ${confirmText} to confirm`}
            value={typed}
            onChange={e => setTyped(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
        </form>
      ) : (
        message
      )}
    </Modal>
  );
}

// ── Prompt / form dialog ────────────────────────────────────────────────────
function PromptDialog({ title = "Enter details", description, fields, submitLabel = "Submit", cancelLabel = "Cancel", variant = "info", onResult }:
  PromptOptions & { onResult: (v: Record<string, string> | null) => void }) {
  const [values, setValues] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const f of fields) init[f.name] = f.defaultValue ?? "";
    return init;
  });
  const [error, setError] = useState("");
  const firstRef = useRef<any>(null);
  useEffect(() => {
    if (firstRef.current) {
      if (typeof firstRef.current.focus === "function") {
        firstRef.current.focus();
      } else if (typeof firstRef.current.element === "function") {
        firstRef.current.element()?.focus();
      }
    }
  }, []);

  const submit = () => {
    for (const f of fields) {
      if (f.required && !String(values[f.name] ?? "").trim()) {
        setError(`${f.label || f.name} is required`);
        return;
      }
    }
    onResult(values);
  };
  const set = (name: string, v: string) => setValues(s => ({ ...s, [name]: v }));

  return (
    <Modal open onClose={() => onResult(null)} title={title} size="sm">
      <form onSubmit={e => { e.preventDefault(); submit(); }} className="space-y-4 mt-2">
        {description && <p className="text-xs text-[var(--d2d-text-muted)]">{description}</p>}
        {fields.map((f, i) => (
          <div key={f.name}>
            {f.type === "textarea" ? (
              <Textarea
                ref={i === 0 ? (firstRef as any) : undefined}
                label={f.label || " "}
                help={f.help}
                required={f.required}
                value={values[f.name]}
                onChange={e => set(f.name, e.target.value)}
                placeholder={f.placeholder}
                rows={3}
                className="w-full text-xs"
              />
            ) : f.type === "select" ? (
              <Select
                ref={i === 0 ? (firstRef as any) : undefined}
                label={f.label || " "}
                help={f.help}
                required={f.required}
                value={values[f.name]}
                onChange={e => set(f.name, e.target.value)}
                options={f.options || []}
                className="w-full text-xs"
              />
            ) : (
              <Input
                ref={i === 0 ? (firstRef as any) : undefined}
                type={f.type === "number" ? "number" : f.type === "password" ? "password" : "text"}
                label={f.label || " "}
                help={f.help}
                required={f.required}
                value={values[f.name]}
                onChange={e => set(f.name, e.target.value)}
                placeholder={f.placeholder}
                className="w-full text-xs"
              />
            )}
          </div>
        ))}
        {error && <p className="text-xs text-[var(--danger-text)]">{error}</p>}
        <div className="flex justify-end gap-2 pt-4 border-t border-[color:var(--d2d-border)]">
          <Button type="button" variant="secondary" size="sm" onClick={() => onResult(null)}>
            {cancelLabel}
          </Button>
          <Button type="submit" size="sm" variant={variant === "danger" ? "danger" : "primary"}>
            {submitLabel}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
